import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { CheckboxList, Typography } from "@platform/design-system";

import { buildTogglePeriodHiddenDiff } from "../grid/buildPatchDiff";

import type { CustomToolPanelProps } from "ag-grid-react";
import type { FinancialsGridContext } from "../grid/types";
import type { PeriodKey } from "@platform/api";

const PERIOD_DISPLAY_NAMES: Record<PeriodKey, string> = {
  current: "Current",
  renewal: "Renewal",
  revisedRenewal: "Revised Renewal",
};

const PERIOD_ORDER: PeriodKey[] = ["current", "renewal", "revisedRenewal"];

export const ColumnsPanel = ({
  api,
}: CustomToolPanelProps): React.JSX.Element => {
  const [, rerender] = useState(0);

  useEffect(() => {
    const handler = (): void => rerender((n) => n + 1);
    api.addEventListener("modelUpdated", handler);

    return () => api.removeEventListener("modelUpdated", handler);
  }, [api]);

  const context = api.getGridOption("context") as FinancialsGridContext;
  const { payload, queueChange, toggleCarrierHidden } = context;

  const periodItems = PERIOD_ORDER.map((period) => ({
    id: period,
    label: PERIOD_DISPLAY_NAMES[period],
    checked: !payload.periods[period].hidden,
  }));

  const carrierItems = [...payload.carriers]
    .sort((a, b) => a.order - b.order)
    .map((carrier) => ({
      id: carrier.id,
      label: carrier.name,
      checked: !carrier.hidden,
    }));

  const handleTogglePeriod = (id: string): void => {
    const period = id as PeriodKey;
    queueChange(
      buildTogglePeriodHiddenDiff(period, payload.periods[period].hidden),
    );
  };

  const handleToggleCarrier = (carrierId: string): void => {
    const carrier = payload.carriers.find((c) => c.id === carrierId);
    if (!carrier) return;
    toggleCarrierHidden(carrierId, carrier.hidden, payload.carriers);
  };

  return (
    <Box
      data-testid="columns-panel"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Typography variant="caption" sx={{ px: 1, pt: 1, display: "block" }}>
          <strong>Periods</strong>
        </Typography>
        <CheckboxList items={periodItems} onToggle={handleTogglePeriod} />

        <Typography variant="caption" sx={{ px: 1, pt: 1.5, display: "block" }}>
          <strong>Carriers</strong>
        </Typography>
        <CheckboxList items={carrierItems} onToggle={handleToggleCarrier} />
      </Box>
    </Box>
  );
};
